<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';

import ArrowLeft from '@/components/svg/ArrowLeft.vue';
import ArrowRight from '@/components/svg/ArrowRight.vue';
import ImageLightbox from '@/components/utilities/ImageLightbox.vue';
import { getImageUrlWithCustomWidth } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';

interface ImageItem {
  imageUrl: string;
  displayOrder?: number;
  alt?: string;
  caption?: string;
}

interface Props {
  images: ImageItem[];
  showLightbox?: boolean;
  showDots?: boolean;
  showArrows?: boolean;
  aspectRatio?: string;
  imageWidth?: number;
  noImageText?: string;
}

const { t } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const props = withDefaults(defineProps<Props>(), {
  showLightbox: true,
  showDots: true,
  showArrows: true,
  aspectRatio: '16/9',
  imageWidth: 600,
  noImageText: undefined,
});

const currentIndex = ref(0);
const carouselContainer = ref<HTMLElement>();

// Variables pour le swipe tactile
const touchStartX = ref(0);
const touchEndX = ref(0);
const isTouch = ref(false);
const minSwipeDistance = 50;

const sortedImages = computed(() => {
  if (!props.images?.length) return [];
  
  return [...props.images].sort((a, b) => 
    (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
  );
});

const currentImage = computed(() => {
  if (!sortedImages.value.length) return null;
  return sortedImages.value[currentIndex.value];
});

const hasMultipleImages = computed(() => {
  return sortedImages.value.length > 1;
});

const displayImageUrl = computed(() => {
  if (!currentImage.value) return '';
  return getImageUrlWithCustomWidth(currentImage.value.imageUrl, props.imageWidth);
});

const noImageMessage = computed(() => {
  return props.noImageText || t('common.noImageAvailable');
});

function goToPrevious() {
  if (!hasMultipleImages.value) return;
  currentIndex.value = currentIndex.value === 0 
    ? sortedImages.value.length - 1 
    : currentIndex.value - 1;
}

function goToNext() {
  if (!hasMultipleImages.value) return;
  currentIndex.value = currentIndex.value === sortedImages.value.length - 1 
    ? 0 
    : currentIndex.value + 1;
}

function goToIndex(index: number) {
  if (index >= 0 && index < sortedImages.value.length) {
    currentIndex.value = index;
  }
}

function handleKeydown(event: KeyboardEvent) {
  if (!hasMultipleImages.value) return;
  
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    goToPrevious();
  } else if (event.key === 'ArrowRight') {
    event.preventDefault();
    goToNext();
  }
}

// Fonctions de gestion du swipe tactile
function handleTouchStart(event: TouchEvent) {
  if (!hasMultipleImages.value) return;
  
  isTouch.value = true;
  touchStartX.value = event.touches[0].clientX;
}

function handleTouchMove(event: TouchEvent) {
  if (!isTouch.value || !hasMultipleImages.value) return;
  
  // Empêcher le scroll vertical pendant le swipe horizontal
  const currentX = event.touches[0].clientX;
  const deltaX = Math.abs(currentX - touchStartX.value);
  
  // Si le mouvement horizontal est significatif, empêcher le scroll vertical
  if (deltaX > 10) {
    event.preventDefault();
  }
}

function handleTouchEnd(event: TouchEvent) {
  if (!isTouch.value || !hasMultipleImages.value) return;
  
  touchEndX.value = event.changedTouches[0].clientX;
  const deltaX = touchStartX.value - touchEndX.value;
  
  if (Math.abs(deltaX) > minSwipeDistance) {
    if (deltaX > 0) {
      // Swipe vers la gauche - image suivante
      goToNext();
    } else {
      // Swipe vers la droite - image précédente
      goToPrevious();
    }
  }
  
  isTouch.value = false;
}

// Lifecycle hooks pour ajouter les event listeners
onMounted(() => {
  if (carouselContainer.value) {
    carouselContainer.value.addEventListener('touchstart', handleTouchStart, { passive: false });
    carouselContainer.value.addEventListener('touchmove', handleTouchMove, { passive: false });
    carouselContainer.value.addEventListener('touchend', handleTouchEnd, { passive: true });
  }
});

onUnmounted(() => {
  if (carouselContainer.value) {
    carouselContainer.value.removeEventListener('touchstart', handleTouchStart);
    carouselContainer.value.removeEventListener('touchmove', handleTouchMove);
    carouselContainer.value.removeEventListener('touchend', handleTouchEnd);
  }
});
</script>

<template>
  <div class="imageCarousel" 
       tabindex="0" 
       @keydown="handleKeydown">
    <div v-if="sortedImages.length > 0" 
         ref="carouselContainer"
         class="imageCarousel-container">
      <!-- Image principale -->
      <div class="imageCarousel-imageContainer">
        <ImageLightbox v-if="showLightbox && currentImage"
                       :display-url="displayImageUrl"
                       :modal-url="currentImage.imageUrl"
                       :alt="currentImage.alt || `Image ${currentIndex + 1}`" 
                       class="imageCarousel-lightbox" />
        <img v-else-if="currentImage"
             :src="displayImageUrl"
             :alt="currentImage.alt || `Image ${currentIndex + 1}`"
             class="imageCarousel-image"
             :style="{ aspectRatio: aspectRatio }" />
        
        <!-- Flèches de navigation -->
        <template v-if="showArrows && hasMultipleImages">
          <button class="imageCarousel-arrow imageCarousel-arrow--left"
                  @click="goToPrevious"
                  :aria-label="t('common.previous')">
            <ArrowLeft class="imageCarousel-arrowIcon" />
          </button>
          <button class="imageCarousel-arrow imageCarousel-arrow--right"
                  @click="goToNext"
                  :aria-label="t('common.next')">
            <ArrowRight class="imageCarousel-arrowIcon" />
          </button>
        </template>
        
        <!-- Caption -->
        <div v-if="currentImage?.caption"
             class="imageCarousel-caption">
          <p class="imageCarousel-captionText">{{ currentImage.caption }}</p>
        </div>
      </div>
      
      <!-- Indicateurs de pagination -->
      <div v-if="showDots && hasMultipleImages" 
           class="imageCarousel-dots">
        <button v-for="(image, index) in sortedImages"
                :key="index"
                class="imageCarousel-dot"
                :class="{ 'imageCarousel-dot--active': index === currentIndex }"
                :aria-label="`${t('common.image')} ${index + 1}`"
                @click="goToIndex(index)">
        </button>
      </div>
    </div>
    
    <!-- Message pas d'images -->
    <div v-else class="imageCarousel-noImages">
      {{ noImageMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.imageCarousel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  outline: none;
}

.imageCarousel-container {
  position: relative;
  width: 100%;
  touch-action: pan-y;
}

.imageCarousel-imageContainer {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: var(--gray-100);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

.imageCarousel-image {
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
}

.imageCarousel-lightbox {
  width: 100%;
  
  :deep(.imageLightbox-image) {
    width: 100%;
    height: auto;
    object-fit: cover;
    aspect-ratio: v-bind(aspectRatio);
    border-radius: 8px;
  }
}

.imageCarousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid var(--gray-200);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-50%) scale(1.05);
    border-color: var(--primary-400);
  }
  
  &--left {
    left: 12px;
  }
  
  &--right {
    right: 12px;
  }
}

.imageCarousel-arrowIcon {
  width: 20px;
  height: 20px;
  color: var(--gray-600);
  
  .imageCarousel-arrow:hover & {
    color: var(--primary-600);
  }
}

.imageCarousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 1rem;
}

.imageCarousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: var(--gray-300);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--gray-400);
    transform: scale(1.1);
  }
  
  &--active {
    background: var(--primary-500);
    
    &:hover {
      background: var(--primary-600);
    }
  }
}

.imageCarousel-noImages {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 200px;
  background: var(--gray-100);
  border: 2px dashed var(--gray-300);
  border-radius: 8px;
  color: var(--gray-500);
  font-size: 1.125rem;
  font-style: italic;
}

.imageCarousel-caption {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 2rem 1rem 1rem;
  border-radius: 0 0 8px 8px;
}

.imageCarousel-captionText {
  margin: 0;
  color: white;
  font-size: 1.25rem;
  font-weight: 500;
  text-align: center;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .imageCarousel-arrow {
    width: 32px;
    height: 32px;
    
    &--left {
      left: 8px;
    }
    
    &--right {
      right: 8px;
    }
  }
  
  .imageCarousel-arrowIcon {
    width: 16px;
    height: 16px;
  }
  
  .imageCarousel-dots {
    gap: 6px;
    margin-top: 0.75rem;
  }
  
  .imageCarousel-dot {
    width: 10px;
    height: 10px;
  }
}
</style>